import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import {
    DetailsFormContainer,
    ButtonGroup,
    FormGroupWrapper,
    FullRow,
    HalfRow,
    LegalInfoWrapper,
    Row
} from './Components';
import Button from '../../components/Button';
import TwoGroup from '../../components/TwoGroup';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import InputGroup from '../InputGroup';
import TextareaGroup from '../TextareaGroup';

import GetUpdatesCheckbox from '../Widget/GetUpdatesCheckbox';
import LegalInfo from '../Widget/LegalInfo';

import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';
import { isPortrait } from '../../utils/size';

import { cacheFormData } from '../../utils/formCaching/actions';
import {
    getCachedFullName,
    findCachedValueByActionIdentifier
} from '../../utils/formCaching/helpers';
import { capitalization, copyFor } from '../../copy/copy';
import validate from './validations';

const namePlaceholder = () => copyFor('placeholders', 'yourName', 'email');
const escapedNamePlaceholder = () =>
    namePlaceholder().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const replacePlaceholders = (value, previousValue, allValues) => {
    let newValue =
        allValues.fullName != null
            ? value.replace(/@userFullName/g, allValues.fullName)
            : value;
    return newValue;
};

class DetailsForm extends React.Component {
    constructor() {
        super();
        this.state = {
            textAreaFocused: false
        };
    }

    toggleTextAreaFocused = () => {
        if (!isPortrait()) {
            setTimeout(
                () => {
                    this.setState({ textAreaFocused: !this.state.textAreaFocused });
                },
                !this.state.textAreaFocused ? 0 : 300
            );
        }
    };

    render() {
        return (
            <DetailsFormContainer onSubmit={this.props.handleSubmit} autocomplete="on">
                <Row className="row-wrapper-1">
                    <HalfRow>
                        <Field
                            className={'fs-block'}
                            name="prefix"
                            component={InputGroup}
                            type="text"
                            label={copyFor(
                                'formFields',
                                'prefix',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'prefix', 'email')}
                        />
                    </HalfRow>
                    <HalfRow>
                        <Field
                            className={'fs-block'}
                            name="fullName"
                            component={InputGroup}
                            type="text"
                            label={copyFor(
                                'formFields',
                                'fullName',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'fullName', 'email')}
                            onBlur={(event, value) =>
                                value != null &&
                                value !== '' &&
                                !this.props.isEmailMessageChanged &&
                                this.props.change(
                                    'message',
                                    this.props.initialValues.message
                                        .replace(
                                            RegExp(escapedNamePlaceholder(), 'g'),
                                            value
                                        )
                                        .replace(
                                            /@localRepresentative/g,
                                            this.props.localRepFullName
                                        )
                                )
                            }
                        />
                    </HalfRow>
                </Row>
                <Row className="row-wrapper-2">
                    <FullRow>
                        <Field
                            className={'fs-block'}
                            name="email"
                            component={InputGroup}
                            type="email"
                            label={copyFor(
                                'formFields',
                                'emailAddress',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'emailAddress', 'email')}
                        />
                    </FullRow>
                </Row>
                <Row className="row-wrapper-3">
                    <FullRow>
                        <Field
                            title="message"
                            name="message"
                            component={TextareaGroup}
                            label={copyFor(
                                'formFields',
                                'message',
                                'email',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'message', 'email')}
                            class="message-response"
                            classLabel="label-response"
                            toggleTextAreaFocused={this.toggleTextAreaFocused}
                            onChange={e => {
                                const templatedVersion = e.target.value.replace(
                                    `${this.props.formattedReps}`,
                                    '@localRepresentative'
                                );
                                this.props.onEmailMessageChange();
                                this.props.cacheFormData(
                                    { message: templatedVersion },
                                    this.props.actionIdentifier
                                );
                            }}
                            normalize={replacePlaceholders}
                        />
                    </FullRow>
                </Row>
                <LegalInfoWrapper>
                    <FormGroupWrapper>
                        <GetUpdatesCheckbox
                            sidebar={true}
                            isEU={this.props.endUserLocation.isEU}
                            labelText={this.props.getUpdatesText}
                        />
                        <LegalInfo align="mobile-right" />
                    </FormGroupWrapper>
                </LegalInfoWrapper>
                <ButtonGroup isSubmitting={this.props.isSubmitting}>
                    <TwoGroup>
                        <Button
                            style={{
                                visibility: this.state.textAreaFocused
                                    ? 'hidden'
                                    : 'visible'
                            }}
                            label={copyFor(
                                'buttons',
                                'back',
                                'email',
                                capitalization.eachWord
                            )}
                            onClick={this.props.onBackFromRepAction}
                            className="middleButton"
                        />
                        <Button
                            primary={true}
                            label={
                                this.state.textAreaFocused
                                    ? copyFor(
                                          'buttons',
                                          'done',
                                          'email',
                                          capitalization.eachWord
                                      )
                                    : copyFor(
                                          'buttons',
                                          'send',
                                          'email',
                                          capitalization.eachWord
                                      )
                            }
                            onClick={e => {
                                e.preventDefault();
                                if (!this.state.textAreaFocused) {
                                    this.props.handleSubmit();
                                }
                            }}
                            type="submit"
                            className="submitButton"
                            disabled={this.props.invalid && !this.state.textAreaFocused}
                            isSubmitting={this.props.isSubmitting}
                        />
                        <LoadingSpinner />
                    </TwoGroup>
                </ButtonGroup>
            </DetailsFormContainer>
        );
    }
}

DetailsForm.propTypes = {
    actionIdentifier: PropTypes.string.isRequired,
    cacheFormData: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    endUserLocation: PropTypes.object.isRequired,
    formattedReps: PropTypes.string,
    getUpdatesText: PropTypes.string,
    handleSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.object.isRequired,
    invalid: PropTypes.bool.isRequired,
    isEmailMessageChanged: PropTypes.bool,
    isSubmitting: PropTypes.bool.isRequired,
    localRepFullName: PropTypes.string,
    onBackFromRepAction: PropTypes.func,
    onEmailMessageChange: PropTypes.func.isRequired
};

export default compose(
    connect(
        state => {
            const reps = state.localRep.localRepData
                ? relevantRepresentatives(
                      state.widget.activeAction.representativeSearch,
                      state.localRep.localRepData.representative
                  )
                : [];
            const emailableReps = reps.filter(rep => rep.canBeEmailed).slice(0, 3);
            const formattedReps = joinEnglish(
                reps
                    .filter(
                        rep =>
                            rep.canBeEmailed &&
                            state.localRep.hiddenRepresentatives.indexOf(rep.fullName) ===
                                -1
                    )
                    .map(rep => rep.fullName || rep.twitterMention)
            );
            const actionIdentifier = state.widget.activeAction.identifier;

            const cachedMessage = findCachedValueByActionIdentifier(
                state.cachedFormData.message,
                actionIdentifier
            );
            const message =
                state.cachedFormData.message.length > 0 && !!cachedMessage
                    ? cachedMessage
                          .replace(/@localRepresentative/g, formattedReps)
                          .replace(/@userFullName/g, namePlaceholder())
                    : state.widget.activeAction.textRaw
                          .replace(/@localRepresentative/g, formattedReps)
                          .replace(/@userFullName/g, namePlaceholder());

            return {
                initialValues: {
                    getUpdates: !(state.widget.endUserLocation || {}).isEU,
                    message,
                    prefix: state.cachedFormData.prefix || '',
                    fullName: getCachedFullName(state),
                    email: state.cachedFormData.email || ''
                },
                formattedReps,
                getUpdatesText: state.widget.activeAction.subscriptionOptInText,
                actionIdentifier,
                endUserLocation: state.widget.endUserLocation || {},
                localRepFullName: joinEnglish(emailableReps.map(rep => rep.fullName))
            };
        },
        {
            cacheFormData
        }
    ),
    reduxForm({
        destroyOnUnmount: false,
        form: 'emailDetailsForm',
        validate
    })
)(DetailsForm);
